import type { OpaqueCursorPagination } from './authorization.types';
import type { JsonObject } from './common.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import type { Role } from './identity-contracts';

export const TransactionLogOperations = [
    'INSERT',
    'UPDATE',
    'DELETE',
    'LOGIN',
    'MFA_SUCCESS',
    'ROLE_ASSIGNMENT_ACTIVATED',
    'LOGOUT_SUCCESS',
    'SESSION_EXPIRED',
    'IMPERSONATION_STARTED',
    'IMPERSONATION_ENDED',
] as const;

export type TransactionLogOperation = (typeof TransactionLogOperations)[number];

export const TransactionLogTableNames = [
    'users',
    'organizations',
    'merchant_customer_records',
    'role_assignments',
    'totp_backup_codes',
    'ipw_customer_links',
    'payment_method_tokens',
    'stored_payment_methods',
    'credit_card_stored_payment_details',
    'debit_card_stored_payment_details',
    'ach_stored_payment_details',
    'payment_requests',
    'payment_request_delivery_events',
    'payment_request_documents',
    'document_templates',
    'payment_transactions',
    'payment_transaction_lifecycle_events',
    'credentials',
    'credential_rotation_attempts',
    'permissions_bundles',
    'permissions_policy_grants',
    'legal_documents',
    'user_legal_document_acceptances',
    'processors',
    'merchant_accounts',
    'logical_terminals',
    'terminal_devices',
    'allowed_website_templates',
    'organization_allowed_websites',
    'ipx_rbs_workflow_instances',
] as const;

export type TransactionLogTableName = (typeof TransactionLogTableNames)[number];

export interface TransactionLogTableRowSnapshot extends JsonObject {}

export interface TransactionLogRowDetails extends JsonObject {}

export interface ForensicUserReference {
    user_uuid: string;
    primary_email: string;
}

export interface ForensicOrganizationReference {
    organization_uuid: string;
    name: string;
}

export interface ForensicRoleAssignmentReference {
    role_assignment_uuid: string;
    role: Role;
    user: ForensicUserReference;
    organization: ForensicOrganizationReference | null;
}

export interface TransactionLogListItemCommon {
    transaction_log_ref: string;
    ts: string;
    actor: ForensicUserReference;
    session_ref: string | null;
    effective_role_assignment: ForensicRoleAssignmentReference | null;
    ip_address: string | null;
    user_agent: string | null;
}

export interface TransactionLogDmlListItemCommon extends TransactionLogListItemCommon {
    table_name: TransactionLogTableName;
    row_details: TransactionLogRowDetails;
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogInsertListItem extends TransactionLogDmlListItemCommon {
    operation: 'INSERT';
}

export interface TransactionLogUpdateListItem extends TransactionLogDmlListItemCommon {
    operation: 'UPDATE';
}

export interface TransactionLogDeleteListItem extends TransactionLogDmlListItemCommon {
    operation: 'DELETE';
}

export interface TransactionLogLoginListItem extends TransactionLogListItemCommon {
    operation: 'LOGIN';
}

export interface TransactionLogMfaSuccessListItem extends TransactionLogListItemCommon {
    operation: 'MFA_SUCCESS';
}

export interface TransactionLogRoleAssignmentActivatedListItem
    extends TransactionLogListItemCommon {
    operation: 'ROLE_ASSIGNMENT_ACTIVATED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogLogoutSuccessListItem extends TransactionLogListItemCommon {
    operation: 'LOGOUT_SUCCESS';
}

export interface TransactionLogSessionExpiredListItem extends TransactionLogListItemCommon {
    operation: 'SESSION_EXPIRED';
}

export interface TransactionLogImpersonationStartedListItem
    extends TransactionLogListItemCommon {
    operation: 'IMPERSONATION_STARTED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogImpersonationEndedListItem
    extends TransactionLogListItemCommon {
    operation: 'IMPERSONATION_ENDED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export type TransactionLogListItem =
    | TransactionLogInsertListItem
    | TransactionLogUpdateListItem
    | TransactionLogDeleteListItem
    | TransactionLogLoginListItem
    | TransactionLogMfaSuccessListItem
    | TransactionLogRoleAssignmentActivatedListItem
    | TransactionLogLogoutSuccessListItem
    | TransactionLogSessionExpiredListItem
    | TransactionLogImpersonationStartedListItem
    | TransactionLogImpersonationEndedListItem;

export const TransactionLogSortFields = [
    'transaction_log_ref',
    'ts',
    'operation',
    'table_name',
    'row_details',
    'ip_address',
    'user_agent',
] as const;

export type TransactionLogSortField = (typeof TransactionLogSortFields)[number];

export const TransactionLogSortDirections = ['asc', 'desc'] as const;

export type TransactionLogSortDirection =
    (typeof TransactionLogSortDirections)[number];

export interface TransactionLogSort {
    field: TransactionLogSortField;
    direction: TransactionLogSortDirection;
    json_path?: string[];
}

export interface TransactionLogSearchRequest extends OpaqueCursorSearchRequest {
    sort?: TransactionLogSort;
}

export interface ListTransactionLogPageResponse {
    transaction_log: TransactionLogListItem[];
    pagination: OpaqueCursorPagination;
}

export interface ForensicOptionSearchRequest {
    terms?: string[];
    limit?: number;
    cursor?: string;
}

export interface ForensicReferenceOption {
    value: string;
    label: string;
}

export interface ForensicSessionOption extends ForensicReferenceOption {
    first_ts: string;
    latest_ts: string;
}

export interface ListForensicReferenceOptionsPageResponse {
    options: ForensicReferenceOption[];
    pagination: OpaqueCursorPagination;
}

export interface ListForensicSessionOptionsPageResponse {
    options: ForensicSessionOption[];
    pagination: OpaqueCursorPagination;
}

export type RecentUserActivityOperation =
    | 'MFA_SUCCESS'
    | 'LOGIN'
    | 'LOGOUT_SUCCESS'
    | 'SESSION_EXPIRED';

export type RecentRoleActivityOperation =
    | 'ROLE_ASSIGNMENT_ACTIVATED'
    | 'IMPERSONATION_STARTED'
    | 'IMPERSONATION_ENDED';

export type RecentActivityUserReference = ForensicUserReference;

export type RecentActivityOrganizationReference = ForensicOrganizationReference;

export type RecentActivityRoleAssignmentReference = ForensicRoleAssignmentReference;

export interface RecentUserActivityListItem {
    activity_ref: string;
    ts: string;
    operation: RecentUserActivityOperation;
    session_ref: string | null;
    user: RecentActivityUserReference;
    effective_role_assignment: RecentActivityRoleAssignmentReference | null;
    ip_address: string | null;
    user_agent: string | null;
}

export interface RecentRoleActivityListItem {
    activity_ref: string;
    ts: string;
    operation: RecentRoleActivityOperation;
    session_ref: string | null;
    actor: RecentActivityUserReference;
    effective_role_assignment: RecentActivityRoleAssignmentReference;
    ip_address: string | null;
    user_agent: string | null;
}

export interface RecentUserActivitySearchRequest {
    user_uuids?: string[];
    limit?: number;
    cursor?: string;
}

export interface RecentRoleActivitySearchRequest {
    user_uuids?: string[];
    organization_uuids?: string[];
    roles?: Role[];
    limit?: number;
    cursor?: string;
}

export interface ListRecentUserActivityPageResponse {
    activity: RecentUserActivityListItem[];
    pagination: OpaqueCursorPagination;
}

export interface ListRecentRoleActivityPageResponse {
    activity: RecentRoleActivityListItem[];
    pagination: OpaqueCursorPagination;
}
